<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search slates..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchSlates"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Slate Status</label>
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="processing">Processing</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">File Status</label>
          <select v-model="filters.file_status">
            <option value="">All</option>
            <option value="uploading">Uploading</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </template>
      <template v-if="$checkPermission('slates.create')" #actions>
        <button @click="showUpload = true" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || slates.length" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow" :class="{ 'animate-fade-in': !loading }">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Slate Status</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">File Status</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Size</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Created</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <!-- Skeleton rows -->
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-100 skeleton-pulse shrink-0" />
                <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" />
              </div>
            </td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-24" /></td>
            <td class="px-6 py-4 text-right"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <!-- Real rows -->
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="slate in slates" :key="slate._id" class="hover:bg-gray-50/50 transition-colors">
            <!-- Name -->
            <td class="px-6 py-4 max-w-[400px]">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M17 7l-2-4H9L7 7"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
                  </svg>
                </div>
                <router-link
                  v-if="!isProcessing(slate) && $checkPermission('slates.update')"
                  :to="{ name: 'slates-edit', params: { id: slate._id } }"
                  class="text-[14px] text-gray-900 hover:text-[var(--color-primary)] transition-colors truncate"
                >{{ slate.title || slate.file_object?.name }}</router-link>
                <span v-else class="text-[14px] text-gray-900 truncate">{{ slate.title || slate.file_object?.name }}</span>
              </div>
            </td>

            <!-- Slate Status -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span :class="slateStatusClass(slate.status)">{{ slate.status || '—' }}</span>
            </td>

            <!-- File Status -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span :class="fileStatusClass(slate.file_object?.status)">{{ slate.file_object?.status || '—' }}</span>
            </td>

            <!-- Size -->
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm font-data text-gray-500">{{ $filters.formatBytes(slate.file_object?.size || 0) }}</span>
            </td>

            <!-- Created -->
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(slate.created_at) }}</span>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4 text-right">
              <div v-if="!isProcessing(slate)" class="flex items-center justify-end gap-1">
                <ActionsMenu :items="slateActions(slate)" />
              </div>
              <div v-else class="flex items-center justify-end">
                <svg class="w-4 h-4 text-gray-400 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83"/>
                </svg>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="search || filters.status || filters.file_status" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching slates found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M17 7l-2-4H9L7 7"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No slates yet</p>
      <p class="text-sm text-gray-500 mt-1 mb-5">Upload your first slate video to get started</p>
      <button v-if="$checkPermission('slates.create')" @click="showUpload = true" class="btn-primary">
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        Upload Slate
      </button>
    </div>

    <!-- Delete confirm -->
    <ConfirmModal
      :show="showDeleteModal" :loading="deleting"
      title="Delete Slate"
      :message="`Are you sure you want to delete '${slateToDelete?.title || slateToDelete?.file_object?.name}'? This action cannot be undone.`"
      confirm-text="Delete"
      :danger="true"
      @confirm="deleteSlate"
      @cancel="showDeleteModal = false"
    />

    <!-- Upload modal -->
    <UploadSlate v-if="showUpload" @closed="showUpload = false" @created="fetchSlates" />

    <!-- Preview modal -->
    <MediaPlayerModal
      :show="showPlayer"
      :title="previewSlate?.title || 'Slate Preview'"
      :formats="previewFormats"
      @close="showPlayer = false; previewSlate = null"
    />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import icons from '../../components/action-icons.js'
import ConfirmModal from '../../components/ConfirmModal.vue'
import MediaPlayerModal from '../../components/MediaPlayerModal.vue'
import UploadSlate from './upload.vue'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

// While any listed slate is still processing/uploading we poll its
// status endpoint on a short interval so the row flips to "success"
// without the user having to refresh.
const STATUS_POLL_INTERVAL_MS = 5000

export default {
  name: 'SlatesView',
  components: { ListToolbar, ActionsMenu, ConfirmModal, MediaPlayerModal, UploadSlate },

  data() {
    return {
      slates: [],
      search: '',
      filters: { status: '', file_status: '' },
      sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'status:1', label: 'Status A-Z' },
        { value: 'status:-1', label: 'Status Z-A' },
        { value: 'file_status:1', label: 'File Status A-Z' },
        { value: 'file_status:-1', label: 'File Status Z-A' },
        { value: 'filesize:1', label: 'File Size (smallest)' },
        { value: 'filesize:-1', label: 'File Size (largest)' },
        { value: 'updated:1', label: 'Last Updated (oldest)' },
        { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 50, page: 1, total: 0 },
      loading: true,
      showUpload: false,
      showPlayer: false,
      previewSlate: null,
      showDeleteModal: false,
      deleting: false,
      slateToDelete: null,
      pollTimer: null
    }
  },

  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(v => v !== '' && v != null).length
    },
    previewFormats() {
      if (!this.previewSlate?._id) return []
      return [{
        id: 'original',
        remoteUrl: this.$mediaUrl.slate(this.previewSlate._id),
        fileSize: this.previewSlate.file_object?.size,
        status: 'success'
      }]
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchSlates() },
    sort() { this.pagination.page = 1; this.fetchSlates() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchSlates() } }
  },

  async mounted() {
    await this.fetchSlates()
  },

  beforeUnmount() {
    this.stopPolling()
  },

  methods: {
    isProcessing(slate) {
      return ['uploading', 'processing'].includes(slate.file_object?.status) || slate.status === 'processing'
    },

    slateStatusClass(status) {
      if (status === 'success') return 'badge-live'
      if (status === 'failed') return 'badge-error'
      if (status === 'processing') return 'badge-warning'
      return 'badge-idle'
    },

    fileStatusClass(status) {
      if (status === 'success') return 'badge-live'
      if (status === 'failed') return 'badge-error'
      if (['uploading', 'processing'].includes(status)) return 'badge-warning'
      return 'badge-idle'
    },

    clearFilters() {
      this.filters = { status: '', file_status: '' }
    },

    slateActions(slate) {
      return [
        { label: 'Edit', show: this.$checkPermission('slates.update'), to: { name: 'slates-edit', params: { id: slate._id } }, icon: icons.edit },
        { label: 'Preview', show: slate.file_object?.status === 'success', action: () => this.showPreview(slate), icon: icons.preview },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('slates.delete'), danger: true, action: () => this.confirmDelete(slate), icon: icons.delete }
      ]
    },

    confirmDelete(slate) {
      this.slateToDelete = slate
      this.showDeleteModal = true
    },

    async deleteSlate() {
      if (!this.slateToDelete) return
      this.deleting = true
      try {
        await api.delete(`/slates/${this.slateToDelete._id}`)
        notifySuccess('Slate deleted')
        this.showDeleteModal = false
        this.slateToDelete = null
        this.fetchSlates()
      } catch { /* toast */ } finally { this.deleting = false }
    },

    showPreview(slate) {
      this.previewSlate = slate
      this.showPlayer = true
    },

    async fetchSlates(count = true) {
      try {
        const params = {
          limit: this.pagination.limit,
          page: this.pagination.page
        }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.file_status) params.file_status = this.filters.file_status
        if (count) params.count = true

        const res = await api.get('/slates', { params })
        this.slates = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
        this.refreshPolling()
      } catch {
        // keep existing data
      } finally {
        this.loading = false
      }
    },

    // Start/stop the poll timer based on whether any row is still
    // processing. Same pattern as the old CMS; there is no websocket
    // event for slate updates, so we short-poll the per-item status
    // endpoint until every visible slate settles.
    refreshPolling() {
      const pending = this.slates.filter(s => this.isProcessing(s))
      if (!pending.length) { this.stopPolling(); return }
      if (this.pollTimer) return
      this.pollTimer = setInterval(this.pollPending, STATUS_POLL_INTERVAL_MS)
    },

    stopPolling() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },

    async pollPending() {
      const pending = this.slates.filter(s => this.isProcessing(s))
      if (!pending.length) { this.stopPolling(); return }
      for (const slate of pending) {
        try {
          const res = await api.get(`/slates/${slate._id}/status`, { silent: true })
          const status = res.data?.status
          if (!status) continue
          const idx = this.slates.findIndex(s => s._id === slate._id)
          if (idx < 0) continue
          const updated = { ...this.slates[idx] }
          updated.status = status.slate
          updated.file_object = { ...updated.file_object, status: status.file }
          updated.url = { ...updated.url, original: status.url }
          this.slates.splice(idx, 1, updated)
        } catch { /* silent */ }
      }
      if (!this.slates.some(s => this.isProcessing(s))) this.stopPolling()
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchSlates(false)
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
